import { Directive, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ReactiveComponent } from '@app/shared/components/reactive-component';
import { FormMappingModelPrototype } from '@app/utils/form-mapping/models/form-mapping-model';
import { loggerFor } from '@app/utils/logger';
import { clearFormArray, updateValidityState } from '@app/utils/reactive-forms';
import { FormArrayTyped, FormGroupTyped } from 'src/types/typed-forms';

@Directive()
export abstract class ReactiveFormArrayComponent<T, ARRAY_MODEL, ENTRY_MODEL>
  extends ReactiveComponent
  implements OnChanges, OnInit
{
  @Input()
  public value: T[] = [];

  @Output()
  public onValueChanged: EventEmitter<T[]> = new EventEmitter();

  public form: FormGroupTyped<ARRAY_MODEL>;

  /**
   * Used in the template for the *ngFor directive
   */
  public formControls: Array<FormGroupTyped<ENTRY_MODEL>>;

  protected logger = loggerFor('ReactiveFormArrayComponent');

  /**
   * Form model to use for the outer form group
   */
  protected abstract arrayFormModel: FormMappingModelPrototype;

  /**
   * Form model to use for each entry
   */
  protected abstract entryFormModel: FormMappingModelPrototype;

  /**
   * Indicates if the entry is indexed (i.e. every entry has an index property)
   */
  protected abstract isIndexed: boolean;

  /**
   * If a form array indexed then the first index will be this index.
   */
  protected startIndex: number = 1;

  /**
   * The property the array of values uses in the arrayFormModel
   */
  protected abstract parameterName: string;

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.parameterName in changes) {
      this.refresh(changes[this.parameterName].currentValue);
    }
  }

  public ngOnInit(): void {
    if (this.form === undefined) {
      this.form = this.arrayFormModel.formGroup();
    }

    this.refresh();
  }

  public add(value?: T, index?: number): void {
    const entry = value ?? this.newEntry();

    // Create new form group
    const formGroup = this.createFormGroup(entry, index);

    // Push new formGroup
    this.getFormArray().push(formGroup);

    this.refreshIndex();
    this.updateFormControls();
    this.validateFormControls();

    // Mark as dirty and run validation
    this.form.markAsDirty();
    this.form.markAsTouched();
    this.onTouched();
    this.somethingChanged();
  }

  protected createFormGroup(value: T, index?: number): FormGroupTyped<ENTRY_MODEL> {
    const formGroup: FormGroupTyped<ENTRY_MODEL> = this.entryFormModel.initialisedFormGroup(value);

    if (this.isIndexed && index != null) {
      formGroup.get('index').setValue(index);
    }

    this.updateFormGroup(formGroup);

    return formGroup;
  }

  protected emitValueChanged() {
    this.validateFormControls();

    let model = this.arrayFormModel.merge(
      {
        [this.parameterName]: this.value,
      },
      this.form.value
    )[this.parameterName];

    model = this.updateModelBeforeEmitting(model);

    // Notify inheriting component
    this.valueChanged(model);

    // Notify reactive forms
    this.onChange(model);

    // Notify basic usage
    this.onValueChanged.emit(model);

    this.form.markAsDirty();

    // onTouched for reactive forms
    this.onTouched();

    this.logger.debug('Emitted new values', model);
  }

  protected changed() {}

  protected refresh(values: T[] = this.value): void {
    clearFormArray(this.getFormArray());

    values
      .map((value, index) => this.createFormGroup(value, index))
      .forEach((formGroup) => {
        this.getFormArray().push(formGroup);
      });

    this.updateFormControls();
    this.refreshIndex();
  }

  /**
   * Can be used to modify the value that is emitted
   *
   * @param model List of merged form models
   */
  protected updateModelBeforeEmitting(model: T[]): T[] {
    return model;
  }

  protected refreshIndex() {
    if (this.isIndexed) {
      const entries: ENTRY_MODEL[] = this.getFormArray().value;

      this.getFormArray().patchValue(
        entries.map((entry: ENTRY_MODEL & { index: number }, index) => {
          entry.index = index + this.startIndex;

          return entry;
        }),
        {
          emitEvent: false,
          onlySelf: true,
        }
      );
    }
  }

  public remove(index: number) {
    this.getFormArray().removeAt(index);
    this.refreshIndex();
    this.updateFormControls();

    this.form.markAsDirty();
    this.somethingChanged();
  }

  public somethingChanged(): void {
    this.logger.debug('Something changed');

    this.validateFormControls();
    this.onValidatorChanged();
    this.emitValueChanged();
    this.changed();
  }

  protected updateFormControls(): void {
    this.formControls = this.getFormArray().controls as Array<FormGroupTyped<ENTRY_MODEL>>;
  }

  protected validateFormControls() {
    this.formControls.forEach((formGroup: FormGroupTyped<ENTRY_MODEL>) => {
      // Update the form group
      updateValidityState(formGroup);
    });
  }

  /**
   * Can be used to set additional validators for each form group
   * @param formGroup
   */
  protected updateFormGroup(formGroup: FormGroupTyped<ENTRY_MODEL>): void {}

  /**
   * Gets called every time the value is emitted
   * @param value
   */
  protected valueChanged(value: T[]): void {}

  public writeValue(obj: any): void {
    this.value = obj || [];

    this.refresh();
  }

  protected abstract getFormArray(): FormArrayTyped<ENTRY_MODEL>;
  protected abstract newEntry(): T;
}
